import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Header,
  SpaceBetween,
  Box,
  Cards,
  Badge,
  Button,
  Alert,
  StatusIndicator,
  Select,
  Table,
  Link,
} from '@cloudscape-design/components';
import { getDataService } from '../services/dataServiceFactory';
import { filterVisibleMappings } from '../services/mappingVisibility';

const Dashboard = () => {
  const PREVIEW_MAPPING_LIMIT = 50;
  const navigate = useNavigate();
  const [mappingsWithReviews, setMappingsWithReviews] = useState([]);
  const [frameworkOptionsMetadata, setFrameworkOptionsMetadata] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState({ label: 'All Statuses', value: 'all' });
  const [awsControlFilter, setAwsControlFilter] = useState({ label: 'All AWS Controls', value: 'all' });
  const [frameworkFilter, setFrameworkFilter] = useState({ label: 'All Frameworks', value: 'all' });
  const [refreshKey, setRefreshKey] = useState(0);
  const [currentPageIndex, setCurrentPageIndex] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Status filter options
  const statusFilterOptions = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Final Approved', value: 'approved' },
    { label: 'Final Rejected', value: 'rejected' },
    { label: 'Yet to be Reviewed', value: 'not-reviewed' },
    { label: 'In Review', value: 'in-review' },
  ];

  // Helper function to determine mapping status
  const getMappingStatus = (mappingWithReviews) => {
    const mappingStatus = mappingWithReviews?.mapping?.status
      ?.toLowerCase()
      ?.replace(/[\s-]/g, '_');
    if (mappingStatus === 'approved' || mappingStatus === 'final_approved') {
      return 'approved';
    }
    if (
      mappingStatus === 'rejected' ||
      mappingStatus === 'final_rejected' ||
      mappingStatus === 'archived'
    ) {
      return 'rejected';
    }
    if (
      mappingStatus === 'pending' ||
      mappingStatus === 'pending_review' ||
      mappingStatus === 'in_review' ||
      mappingStatus === 'labeling_in_progress'
    ) {
      return 'in-review';
    }
    if (mappingStatus === 'not_reviewed') {
      return 'not-reviewed';
    }

    const { reviews } = mappingWithReviews;

    if (!reviews || reviews.length === 0) {
      return 'not-reviewed';
    }

    // Check for final decisions
    const hasFinalApprove = reviews.some(r => r.decision === 'final-approve');
    const hasFinalReject = reviews.some(r => r.decision === 'final-reject');

    if (hasFinalApprove) return 'approved';
    if (hasFinalReject) return 'rejected';

    // Has reviews but no final decision
    return 'in-review';
  };

  // Helper function to parse control key
  const parseControlKey = (key) => {
    if (!key) return { catalog: '', version: '', controlId: '' };
    const parts = key.split('#');
    return {
      catalog: parts[0] || '',
      version: parts[1] || '',
      controlId: parts.slice(2).join('#') || key,
    };
  };

  const isAwsCatalog = (catalog) => /^AWS[.-]?/i.test(catalog || '');
  const isCommonControlsCatalog = (catalog) =>
    (catalog || '').replace(/[-_]/g, '').toLowerCase() === 'commoncontrols';

  const getMappingSides = (mapping) => {
    const left = parseControlKey(mapping?.controlKey);
    const right = parseControlKey(mapping?.mappedControlKey);
    const leftIsAws = isAwsCatalog(left.catalog);
    const rightIsAws = isAwsCatalog(right.catalog);

    if (leftIsAws && !rightIsAws) {
      return {
        aws: { parsed: left, controlKey: mapping?.controlKey },
        framework: { parsed: right, controlKey: mapping?.mappedControlKey },
      };
    }

    if (!leftIsAws && rightIsAws) {
      return {
        aws: { parsed: right, controlKey: mapping?.mappedControlKey },
        framework: { parsed: left, controlKey: mapping?.controlKey },
      };
    }

    return {
      aws: leftIsAws
        ? { parsed: left, controlKey: mapping?.controlKey }
        : { parsed: right, controlKey: mapping?.mappedControlKey },
      framework: !leftIsAws
        ? { parsed: left, controlKey: mapping?.controlKey }
        : { parsed: right, controlKey: mapping?.mappedControlKey },
    };
  };

  // Helper function to group mappings by AWS Control
  const groupByAwsControl = (mappingsData) => {
    const grouped = {};

    mappingsData.forEach(item => {
      const awsControlKey = getMappingSides(item.mapping).aws?.controlKey;
      if (!awsControlKey) {
        console.warn('Skipping mapping with missing controlKey:', item);
        return;
      }

      if (!grouped[awsControlKey]) {
        grouped[awsControlKey] = {
          awsControlKey,
          mappings: [],
          approved: 0,
          rejected: 0,
          notReviewed: 0,
          inReview: 0,
        };
      }

      const status = getMappingStatus(item);
      grouped[awsControlKey].mappings.push(item);
      
      if (status === 'approved') grouped[awsControlKey].approved++;
      else if (status === 'rejected') grouped[awsControlKey].rejected++;
      else if (status === 'not-reviewed') grouped[awsControlKey].notReviewed++;
      else if (status === 'in-review') grouped[awsControlKey].inReview++;
    });

    return Object.values(grouped);
  };

  // Load mappings data (via DynamoDB or REST API based on environment)
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        const dataService = getDataService();
        const [data, frameworks] = await Promise.all([
          dataService.getAllMappingsWithReviews({
            limit: PREVIEW_MAPPING_LIMIT,
            includeReviews: false,
          }),
          dataService.getAvailableFrameworks().catch(() => []),
        ]);
        const visible = await filterVisibleMappings(data);
        console.log('Dashboard loaded mappings:', visible.length, '(pre-filter:', data.length, ')');
        setMappingsWithReviews(visible);
        const frameworkOptions = frameworks
          .filter(
            (framework) =>
              framework?.frameworkName &&
              framework?.version &&
              !isAwsCatalog(framework.frameworkName) &&
              !isCommonControlsCatalog(framework.frameworkName)
          )
          .map((framework) => ({
            label: `${framework.frameworkName} ${framework.version}`,
            value: `${framework.frameworkName}#${framework.version}`,
          }))
          .sort((a, b) => a.label.localeCompare(b.label));
        setFrameworkOptionsMetadata(frameworkOptions);
        setError(null);
      } catch (err) {
        console.error('Error loading dashboard data:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [refreshKey]);

  // Get unique AWS Controls for filter
  const getAwsControlOptions = () => {
    const controls = new Set();
    mappingsWithReviews.forEach(item => {
      const awsControlKey = getMappingSides(item.mapping).aws?.controlKey;
      if (awsControlKey) {
        controls.add(awsControlKey);
      }
    });
    
    const options = [{ label: 'All AWS Controls', value: 'all' }];
    Array.from(controls).sort().forEach(key => {
      const parsed = parseControlKey(key);
      options.push({
        label: parsed.controlId,
        value: key,
      });
    });
    return options;
  };

  // Get unique Frameworks for filter
  const getFrameworkOptions = () => {
    if (frameworkOptionsMetadata.length > 0) {
      return [{ label: 'All Frameworks', value: 'all' }, ...frameworkOptionsMetadata];
    }

    const frameworks = new Map();
    mappingsWithReviews.forEach(item => {
      const framework = getMappingSides(item.mapping).framework;
      const parsed = framework?.parsed;
      if (parsed?.catalog && parsed?.version && !isCommonControlsCatalog(parsed.catalog)) {
        const frameworkKey = `${parsed.catalog}#${parsed.version}`;
        frameworks.set(frameworkKey, {
          label: `${parsed.catalog} ${parsed.version}`,
          value: frameworkKey,
        });
      }
    });

    return [{ label: 'All Frameworks', value: 'all' }, ...Array.from(frameworks.values())];
  };

  // Filter mappings based on selected filters
  const getFilteredMappings = () => {
    return mappingsWithReviews.filter(item => {
      // Status filter
      if (statusFilter.value !== 'all') {
        const status = getMappingStatus(item);
        if (status !== statusFilter.value) return false;
      }

      // AWS Control filter
      if (awsControlFilter.value !== 'all') {
        const awsControlKey = getMappingSides(item.mapping).aws?.controlKey;
        if (awsControlKey !== awsControlFilter.value) return false;
      }

      // Framework filter
      if (frameworkFilter.value !== 'all') {
        const framework = getMappingSides(item.mapping).framework;
        const parsed = framework?.parsed;
        const frameworkKey = `${parsed?.catalog || ''}#${parsed?.version || ''}`;
        if (frameworkKey !== frameworkFilter.value) return false;
      }

      return true;
    });
  };

  // Calculate statistics
  const getStats = () => {
    const filteredMappings = getFilteredMappings();
    const groupedByControl = groupByAwsControl(filteredMappings);

    const totalMappings = filteredMappings.length;
    const totalAwsControls = groupedByControl.length;
    
    let approved = 0;
    let rejected = 0;
    let notReviewed = 0;
    let inReview = 0;

    filteredMappings.forEach(item => {
      const status = getMappingStatus(item);
      if (status === 'approved') approved++;
      else if (status === 'rejected') rejected++;
      else if (status === 'not-reviewed') notReviewed++;
      else if (status === 'in-review') inReview++;
    });

    return {
      totalMappings,
      totalAwsControls,
      approved,
      rejected,
      notReviewed,
      inReview,
    };
  };

  // Navigate to review table with filter
  const navigateToReview = (filterStatus = null, awsControl = null) => {
    const state = {};
    if (filterStatus) state.statusFilter = filterStatus;
    if (awsControl) state.awsControlFilter = awsControl;
    
    navigate('/review', { state: Object.keys(state).length > 0 ? state : undefined });
  };

  // Get status indicator
  const getStatusIndicator = (status) => {
    switch (status) {
      case 'approved':
        return <StatusIndicator type="success">Approved</StatusIndicator>;
      case 'rejected':
        return <StatusIndicator type="error">Rejected</StatusIndicator>;
      case 'in-review':
        return <StatusIndicator type="in-progress">In Review</StatusIndicator>;
      case 'not-reviewed':
        return <StatusIndicator type="pending">Not Reviewed</StatusIndicator>;
      default:
        return <StatusIndicator type="pending">Unknown</StatusIndicator>;
    }
  };

  const stats = getStats();
  const filteredMappings = getFilteredMappings();

  if (loading) {
    return (
      <Container>
        <Box textAlign="center" padding="xl">
          <div className="loading-spinner"></div>
          <Box variant="p" margin={{ top: 'm' }}>
            Loading dashboard...
          </Box>
        </Box>
      </Container>
    );
  }

  return (
    <Container
      header={
        <Header 
          variant="h1" 
          description="Overview of compliance framework mapping reviews"
          actions={
            <SpaceBetween direction="horizontal" size="xs">
              <Button 
                iconName="refresh" 
                onClick={() => setRefreshKey(prev => prev + 1)}
              >
                Refresh
              </Button>
              <Button variant="primary" onClick={() => navigateToReview()}>
                Go to Review Table
              </Button>
            </SpaceBetween>
          }
        >
          Dashboard
        </Header>
      }
    >
      <SpaceBetween size="l">
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Filter Controls */}
        <Container>
          <SpaceBetween direction="horizontal" size="m">
            <Box variant="awsui-key-label">Filters:</Box>
            <Select
              selectedOption={statusFilter}
              onChange={({ detail }) => setStatusFilter(detail.selectedOption)}
              options={statusFilterOptions}
              placeholder="Status"
              selectedAriaLabel="Selected status"
            />
            <Select
              selectedOption={awsControlFilter}
              onChange={({ detail }) => setAwsControlFilter(detail.selectedOption)}
              options={getAwsControlOptions()}
              placeholder="AWS Control"
              selectedAriaLabel="Selected AWS Control"
              filteringType="auto"
            />
            <Select
              selectedOption={frameworkFilter}
              onChange={({ detail }) => setFrameworkFilter(detail.selectedOption)}
              options={getFrameworkOptions()}
              placeholder="Framework"
              selectedAriaLabel="Selected Framework"
              filteringType="auto"
            />
            <Button
              onClick={() => {
                setStatusFilter({ label: 'All Statuses', value: 'all' });
                setAwsControlFilter({ label: 'All AWS Controls', value: 'all' });
                setFrameworkFilter({ label: 'All Frameworks', value: 'all' });
              }}
            >
              Clear Filters
            </Button>
          </SpaceBetween>
          <Box margin={{ top: 's' }}>
            <Badge color="blue">
              Preview mode: showing up to {PREVIEW_MAPPING_LIMIT} mappings
            </Badge>
          </Box>
        </Container>

        {/* Compact Statistics Cards */}
        <Cards
          cardDefinition={{
            header: item => (
              <Box variant="h3" fontSize="heading-s">
                {item.title}
              </Box>
            ),
            sections: [
              {
                id: 'value',
                content: item => (
                  <Box fontSize="heading-xl" fontWeight="bold" color={item.color}>
                    {item.value}
                  </Box>
                ),
              },
            ],
          }}
          items={[
            {
              title: 'Total Mappings',
              value: stats.totalMappings,
              color: 'text-status-info',
            },
            {
              title: 'AWS Controls',
              value: stats.totalAwsControls,
              color: 'text-status-info',
            },
            {
              title: 'Final Approved',
              value: stats.approved,
              color: 'text-status-success',
            },
            {
              title: 'Final Rejected',
              value: stats.rejected,
              color: 'text-status-error',
            },
            {
              title: 'In Review',
              value: stats.inReview,
              color: 'text-status-warning',
            },
            {
              title: 'Not Reviewed',
              value: stats.notReviewed,
              color: 'text-status-pending',
            },
          ]}
          cardsPerRow={[{ cards: 2 }, { minWidth: 500, cards: 3 }, { minWidth: 900, cards: 6 }]}
        />

        {/* Mappings Table */}
        <Table
          onSelectionChange={() => {}}
          columnDefinitions={[
            {
              id: 'awsControl',
              header: 'AWS Control',
              cell: item => {
                const { aws } = getMappingSides(item.mapping);
                const parsed = aws?.parsed || parseControlKey(item.mapping.controlKey);
                return (
                  <Link
                    onFollow={() =>
                      navigateToReview(null, aws?.controlKey || item.mapping.controlKey)
                    }
                  >
                    {parsed.controlId}
                  </Link>
                );
              },
              sortingField: 'awsControl',
              width: 200,
            },
            {
              id: 'framework',
              header: 'Framework',
              cell: item => {
                const { framework } = getMappingSides(item.mapping);
                const parsed = framework?.parsed || parseControlKey(item.mapping.mappedControlKey);
                return (
                  <Badge color="blue">
                    {parsed.catalog} {parsed.version}
                  </Badge>
                );
              },
              sortingField: 'framework',
              width: 150,
            },
            {
              id: 'frameworkControl',
              header: 'Framework Control',
              cell: item => {
                const { framework } = getMappingSides(item.mapping);
                const parsed = framework?.parsed || parseControlKey(item.mapping.mappedControlKey);
                return parsed.controlId;
              },
              sortingField: 'frameworkControl',
              width: 200,
            },
            {
              id: 'status',
              header: 'Review Status',
              cell: item => {
                const status = getMappingStatus(item);
                return getStatusIndicator(status);
              },
              sortingField: 'status',
              width: 150,
            },
            {
              id: 'reviewCount',
              header: 'Review Count',
              cell: item => (
                <Box textAlign="center">
                  {item.reviews?.length || 0}
                </Box>
              ),
              sortingField: 'reviewCount',
              width: 120,
            },
            {
              id: 'lastReviewer',
              header: 'Last Reviewer',
              cell: item => {
                if (!item.reviews || item.reviews.length === 0) {
                  return <Box color="text-body-secondary">-</Box>;
                }
                const latestReview = item.reviews[0];
                return (
                  <SpaceBetween size="xxs">
                    <Box variant="small">{latestReview.reviewerName}</Box>
                    <Box variant="small" color="text-body-secondary">
                      {new Date(latestReview.submittedAt).toLocaleDateString()}
                    </Box>
                  </SpaceBetween>
                );
              },
              width: 150,
            },
          ]}
          items={filteredMappings}
          loadingText="Loading mappings"
          sortingDisabled={false}
          empty={
            <Box textAlign="center" padding="l">
              <SpaceBetween size="m">
                <Box variant="p" color="text-body-secondary">
                  No mappings found with the selected filter.
                </Box>
                <Button onClick={() => setStatusFilter({ label: 'All Statuses', value: 'all' })}>
                  Clear Filter
                </Button>
              </SpaceBetween>
            </Box>
          }
          header={
            <Header
              counter={`(${filteredMappings.length})`}
              description="Click on AWS Control to view in review table"
            >
              Mappings Overview
            </Header>
          }
        />
      </SpaceBetween>
    </Container>
  );
};

export default Dashboard;
